/*****************************************************************
 *  ZOHO BRIDGE PANEL — THE "WAITING" TAB (the holding pile)       *
 *  File: apps/web/src/pages/bridge/InboxTab.js                    *
 *  What it does: filters (status, type, full/thin fields, name),  *
 *  one PAGE of record cards, "Select all on this page" with       *
 *  "Approve selected", and "Approve all that match" with a        *
 *  progress line. The Properties tab uses this same tab with the  *
 *  type locked to properties.                                     *
 *  Reads: GET /inbox, /inbox/approve-all/count, /run.             *
 *  Writes: approve-many and approve-all, only on confirm.         *
 *****************************************************************/

/*****************************************************************
 *  TABLE OF CONTENTS                                              *
 *  SECTION 1 — THE FILTER CHOICES                                 *
 *  SECTION 2 — THE FILTER BAR                                     *
 *  SECTION 3 — "APPROVE ALL THAT MATCH" BOX                       *
 *  SECTION 4 — THE TAB                                            *
 *****************************************************************/

import React, { useCallback, useEffect, useRef, useState } from 'react';
import InboxCard from './InboxCard';
import { BridgeIcon } from './bridgeIcons';
import { BRIDGE_WORDS, STATUS_NAMES, KIND_NAMES } from './bridgeWords';
import { LoadState, WhyBox, Pager } from './bridgeParts';
import {
  fetchPile,
  approveManyRecords,
  fetchApproveAllCount,
  startApproveAll,
  fetchApproveAllRun,
  stopApproveAll,
} from './bridgeApi';

/*****************************************************************
 *  SECTION 1 — THE FILTER CHOICES                                 *
 *****************************************************************/

// The statuses, in the order a person thinks about them.
const STATUS_CHOICES = ['proposed', 'failed', 'waiting', 'applied', 'rejected', 'superseded', 'all'];

// The kinds of record: people first, then places, then prices.
const KIND_CHOICES = ['crm_contact', 'crm_property', 'books_item'];

// The statuses a person can still decide on (same list as the server).
const DECIDABLE_STATUSES = ['proposed', 'failed', 'waiting'];

// One page of cards. WHY (2026-10-13, defect 371): never more than the
// 200 that "Approve selected" accepts, so "Select all on this page" always fits.
const CARDS_PER_PAGE = 50;

// How often the progress line asks how far "approve all" has got.
const RUN_CHECK_MILLISECONDS = 2000;

/*****************************************************************
 *  SECTION 2 — THE FILTER BAR                                     *
 *****************************************************************/

/* ---- FilterBar({ filters, lockedKind, onChange }) --------------
   The Show / Type / Fields selects and the name search.
   onChange(newFilters) is called with the whole set.            */
function FilterBar({ filters, lockedKind, onChange }) {
  const [searchDraft, setSearchDraft] = useState(filters.search || '');
  const showsContacts = !lockedKind || lockedKind === 'crm_contact';

  /* ---- setOne(name, value) -- change one filter, back to page 1 ---- */
  function setOne(filterName, filterValue) {
    onChange({ ...filters, [filterName]: filterValue });
  }

  /* ---- runSearch(event) -- apply the typed name ---- */
  function runSearch(event) {
    event.preventDefault();
    setOne('search', searchDraft.trim());
  }

  return (
    <div className="zb-filters">
      <label className="zb-field">
        <span className="zb-label">{BRIDGE_WORDS.filterStatus}</span>
        <select className="zb-select" value={filters.status} onChange={(event) => setOne('status', event.target.value)}>
          {STATUS_CHOICES.map((oneStatus) => (
            <option key={oneStatus} value={oneStatus}>{STATUS_NAMES[oneStatus]}</option>
          ))}
        </select>
      </label>

      {!lockedKind ? (
        <label className="zb-field">
          <span className="zb-label">{BRIDGE_WORDS.filterKind}</span>
          <select className="zb-select" value={filters.kind} onChange={(event) => setOne('kind', event.target.value)}>
            <option value="">{BRIDGE_WORDS.filterAllKinds}</option>
            {KIND_CHOICES.map((oneKind) => (
              <option key={oneKind} value={oneKind}>{KIND_NAMES[oneKind]}</option>
            ))}
          </select>
        </label>
      ) : null}

      {showsContacts ? (
        <label className="zb-field">
          <span className="zb-label">{BRIDGE_WORDS.filterBadge}</span>
          <select className="zb-select" value={filters.badge} onChange={(event) => setOne('badge', event.target.value)}>
            <option value="">{BRIDGE_WORDS.filterAnyBadge}</option>
            <option value="full">{BRIDGE_WORDS.fullFields}</option>
            <option value="thin">{BRIDGE_WORDS.thinFields}</option>
          </select>
        </label>
      ) : null}

      <form className="zb-field zb-field-grow" onSubmit={runSearch} role="search">
        <label className="zb-label" htmlFor="zb-pile-search">{BRIDGE_WORDS.filterSearch}</label>
        <div className="zb-row zb-row-tight">
          <input
            id="zb-pile-search"
            className="zb-input"
            type="search"
            value={searchDraft}
            onChange={(event) => setSearchDraft(event.target.value)}
          />
          <button type="submit" className="zb-button zb-button-plain">
            <BridgeIcon name="search" />
            <span>{BRIDGE_WORDS.search}</span>
          </button>
        </div>
      </form>
    </div>
  );
}

/*****************************************************************
 *  SECTION 3 — "APPROVE ALL THAT MATCH" BOX                       *
 *****************************************************************/

/* ---- describeRun(runView) -- the progress line in words ---- */
function describeRun(runView) {
  const counts = runView.counts || {};
  let stateWord = BRIDGE_WORDS.approveAllRunning;

  if (runView.state === 'finished') {
    stateWord = BRIDGE_WORDS.approveAllFinished;
  } else if (runView.state === 'stopped') {
    stateWord = BRIDGE_WORDS.approveAllStopped;
  }

  return stateWord + ': ' + runView.done.toLocaleString() + ' ' + BRIDGE_WORDS.of + ' '
    + runView.total.toLocaleString() + '. '
    + counts.applied + ' put into Butler, '
    + counts.waiting + ' waiting for their customer, '
    + counts.failed + ' need a person, '
    + counts.olderVersion + ' older than what Butler has, '
    + counts.skipped + ' already decided.';
}

/* ---- ApproveAllBox({ filters, onFinished }) --------------------
   Counts what matches, asks for one "why", starts the run and
   shows its progress until it ends.                             */
function ApproveAllBox({ filters, onFinished }) {
  const [matchingCount, setMatchingCount] = useState(null);  // how many match now
  const [isWhyOpen, setIsWhyOpen] = useState(false);          // the confirm box
  const [isBusy, setIsBusy] = useState(false);
  const [runView, setRunView] = useState(null);              // the server's progress
  const [problemText, setProblemText] = useState('');
  const timerRef = useRef(null);

  /* ---- countNow() -- how many rows the filters mean ---- */
  const countNow = useCallback(async () => {
    const answer = await fetchApproveAllCount(filters);

    setMatchingCount(answer.ok ? answer.data.matchingCount : null);
  }, [filters]);

  /* ---- watchRun() -- ask how far the run has got, until it ends ---- */
  const watchRun = useCallback(async () => {
    const answer = await fetchApproveAllRun();
    const latestRun = answer.ok ? answer.data : null;

    setRunView(latestRun);

    if (latestRun && latestRun.state === 'running') {
      timerRef.current = setTimeout(watchRun, RUN_CHECK_MILLISECONDS);
      return;
    }

    onFinished();
    countNow();
  }, [countNow, onFinished]);

  useEffect(() => {
    countNow();
  }, [countNow]);

  // a run started earlier (or by someone else) is picked up on open
  useEffect(() => {
    fetchApproveAllRun().then((answer) => {
      const latestRun = answer.ok ? answer.data : null;

      if (latestRun && latestRun.state === 'running') {
        setRunView(latestRun);
        timerRef.current = setTimeout(watchRun, RUN_CHECK_MILLISECONDS);
      }
    });

    return () => clearTimeout(timerRef.current);
  }, [watchRun]);

  /* ---- confirmAll(whyText) -- start the run ---- */
  async function confirmAll(whyText) {
    setIsBusy(true);
    setProblemText('');

    const answer = await startApproveAll(filters, matchingCount, whyText);

    setIsBusy(false);
    setIsWhyOpen(false);

    if (!answer.ok) {
      setProblemText(answer.message || BRIDGE_WORDS.loadFailed);
      countNow();
      return;
    }

    setRunView(answer.data);
    timerRef.current = setTimeout(watchRun, RUN_CHECK_MILLISECONDS);
  }

  /* ---- askToStop() -- stop after the row it is on ---- */
  async function askToStop() {
    const answer = await stopApproveAll();

    if (answer.ok) {
      setRunView(answer.data);
    }
  }

  const isRunning = Boolean(runView) && runView.state === 'running';
  const canStart = !isRunning && Number(matchingCount) > 0;

  return (
    <div className="zb-block zb-approve-all">
      <div className="zb-row">
        <button
          type="button"
          className="zb-button zb-button-plain"
          disabled={!canStart}
          onClick={() => setIsWhyOpen(true)}
        >
          <BridgeIcon name="stack" />
          <span>
            {BRIDGE_WORDS.approveAllThatMatch}
            {matchingCount !== null ? ' (' + Number(matchingCount).toLocaleString() + ')' : ''}
          </span>
        </button>
        {isRunning ? (
          <button type="button" className="zb-button zb-button-plain" onClick={askToStop}>
            <BridgeIcon name="stop" />
            <span>{BRIDGE_WORDS.approveAllStop}</span>
          </button>
        ) : null}
      </div>

      {isWhyOpen ? (
        <>
          <p className="zb-quiet">{BRIDGE_WORDS.approveAllExplain}</p>
          <WhyBox
            confirmWord={BRIDGE_WORDS.approveAllConfirm + ' ' + Number(matchingCount).toLocaleString()}
            tone="primary"
            busy={isBusy}
            onConfirm={confirmAll}
            onCancel={() => setIsWhyOpen(false)}
          />
        </>
      ) : null}

      {problemText ? <div className="zb-notice zb-notice-bad" role="alert"><span>{problemText}</span></div> : null}

      {runView ? (
        <div className={'zb-notice ' + (isRunning ? 'zb-notice-info' : 'zb-notice-ok')} role="status">
          <BridgeIcon name={isRunning ? 'clock' : 'check'} />
          <span>{describeRun(runView)}</span>
        </div>
      ) : null}
    </div>
  );
}

/*****************************************************************
 *  SECTION 4 — THE TAB                                            *
 *****************************************************************/

/* ---- InboxTab({ onChanged, lockedKind }) -----------------------
   onChanged() tells the page to refresh its counts.
   lockedKind (optional) fixes the type, e.g. 'crm_property'.    */
export default function InboxTab({ onChanged, lockedKind }) {
  const [filters, setFilters] = useState({
    status: 'proposed',
    kind: lockedKind || '',
    badge: '',
    search: '',
  });
  const [pageNumber, setPageNumber] = useState(1);              // which page
  const [pileRows, setPileRows] = useState([]);                 // the cards on this page
  const [totalRows, setTotalRows] = useState(0);                // how many in all
  const [loadState, setLoadState] = useState('loading');        // loading / ready / error
  const [selectedIds, setSelectedIds] = useState([]);           // ticked cards
  const [isBulkOpen, setIsBulkOpen] = useState(false);          // the shared why box
  const [isBulkBusy, setIsBulkBusy] = useState(false);
  const [bulkAnswer, setBulkAnswer] = useState('');             // what approve-many did

  /* ---- loadPile(isQuiet) -- read one page for the current filters.
     isQuiet = true keeps the cards on screen while it reloads. ---- */
  const loadPile = useCallback(async (isQuiet) => {
    if (!isQuiet) {
      setLoadState('loading');
    }

    const answer = await fetchPile({ ...filters, page: pageNumber, pageSize: CARDS_PER_PAGE });

    if (!answer.ok || !answer.data) {
      setLoadState('error');
      return;
    }

    setPileRows(Array.isArray(answer.data.rows) ? answer.data.rows : []);
    setTotalRows(Number(answer.data.total) || 0);
    setSelectedIds([]);
    setLoadState('ready');
  }, [filters, pageNumber]);

  useEffect(() => {
    loadPile(false);
  }, [loadPile]);

  /* ---- changeFilters(newFilters) -- new filters start on page 1 ---- */
  function changeFilters(newFilters) {
    setFilters({ ...newFilters, kind: lockedKind || newFilters.kind });
    setPageNumber(1);
    setBulkAnswer('');
  }

  /* ---- afterDecision() -- reload the page and the page counts ---- */
  const afterDecision = useCallback(() => {
    loadPile(true);
    onChanged();
  }, [loadPile, onChanged]);

  /* ---- toggleOne(id) -- tick / untick one card ---- */
  function toggleOne(recordId) {
    const isTicked = selectedIds.includes(recordId);

    if (isTicked) {
      setSelectedIds(selectedIds.filter((oneId) => oneId !== recordId));
    } else {
      setSelectedIds(selectedIds.concat(recordId));
    }
  }

  /* ---- tickAllOnPage() -- tick every card on this page that can be decided ---- */
  function tickAllOnPage() {
    const decidableIds = pileRows
      .filter((oneRow) => DECIDABLE_STATUSES.includes(oneRow.status))
      .map((oneRow) => oneRow.id);

    setSelectedIds(decidableIds);
  }

  /* ---- approveTheTicked(whyText) -- approve-many, then say what happened ---- */
  async function approveTheTicked(whyText) {
    setIsBulkBusy(true);

    const answer = await approveManyRecords(selectedIds, whyText);

    setIsBulkBusy(false);
    setIsBulkOpen(false);

    if (!answer.ok) {
      setBulkAnswer(answer.message || BRIDGE_WORDS.loadFailed);
      return;
    }

    // honest UI: each reason counted on its own (defect 376)
    const counts = answer.data.counts;
    setBulkAnswer(
      counts.applied + ' put into Butler, '
      + (counts.waiting || 0) + ' waiting for their customer, '
      + counts.failed + ' need a person, '
      + (counts.olderVersion || 0) + ' older than what Butler has (still waiting), '
      + counts.skipped + ' skipped (already decided).'
    );
    afterDecision();
  }

  const canDecideHere = DECIDABLE_STATUSES.includes(filters.status);
  const titleWord = lockedKind === 'crm_property' ? BRIDGE_WORDS.tabProperties : BRIDGE_WORDS.tabInbox;

  return (
    <section aria-labelledby="zb-tab-inbox-title">
      <h2 id="zb-tab-inbox-title" className="zb-visually-hidden">{titleWord}</h2>

      <FilterBar filters={filters} lockedKind={lockedKind} onChange={changeFilters} />

      {canDecideHere ? <ApproveAllBox filters={filters} onFinished={afterDecision} /> : null}

      {canDecideHere && pileRows.length > 0 ? (
        <div className="zb-bulkbar">
          <button type="button" className="zb-button zb-button-plain" onClick={tickAllOnPage}>
            {BRIDGE_WORDS.selectAll}
          </button>
          <span className="zb-small">{selectedIds.length} {BRIDGE_WORDS.selectedCount}</span>
          <button
            type="button"
            className="zb-button zb-button-primary"
            disabled={selectedIds.length === 0}
            onClick={() => setIsBulkOpen(true)}
          >
            <BridgeIcon name="check" />
            <span>{BRIDGE_WORDS.approveSelected} ({selectedIds.length})</span>
          </button>
        </div>
      ) : null}

      {isBulkOpen ? (
        <WhyBox
          confirmWord={BRIDGE_WORDS.confirmApprove + ' (' + selectedIds.length + ')'}
          tone="primary"
          busy={isBulkBusy}
          onConfirm={approveTheTicked}
          onCancel={() => setIsBulkOpen(false)}
        />
      ) : null}

      {bulkAnswer ? <div className="zb-notice zb-notice-info" role="status"><span>{bulkAnswer}</span></div> : null}

      {loadState !== 'ready' ? (
        <LoadState state={loadState} onRetry={() => loadPile(false)} />
      ) : null}

      {loadState === 'ready' && pileRows.length === 0 ? (
        <LoadState state="empty" emptyWord={BRIDGE_WORDS.emptyPile} />
      ) : null}

      {loadState === 'ready' && totalRows > 0 ? (
        <Pager page={pageNumber} pageSize={CARDS_PER_PAGE} total={totalRows} onPage={setPageNumber} />
      ) : null}

      {loadState === 'ready' ? (
        <div className="zb-cards">
          {pileRows.map((oneRow) => (
            <InboxCard
              key={oneRow.id}
              record={oneRow}
              isSelected={selectedIds.includes(oneRow.id)}
              onToggleSelect={toggleOne}
              onDecided={afterDecision}
            />
          ))}
        </div>
      ) : null}

      {loadState === 'ready' && totalRows > CARDS_PER_PAGE ? (
        <Pager page={pageNumber} pageSize={CARDS_PER_PAGE} total={totalRows} onPage={setPageNumber} />
      ) : null}
    </section>
  );
}
